/**
 * Client-side commands: navigation targets + deep-link create actions.
 * Rendered instantly above server sections. Every url MUST be a value from
 * ROUTES (enforced by static-commands.test.ts) and parameter-free.
 */
import {
    BarChart3,
    BookOpen,
    Bot,
    Calendar,
    Clock,
    CreditCard,
    FileText,
    Import,
    Inbox as InboxIcon,
    KeyRound,
    LayoutDashboard,
    CalendarDays,
    Link2,
    ListChecks,
    ListOrdered,
    Megaphone,
    MessageCircle,
    MessageSquareText,
    Radio,
    Plus,
    Settings,
    Tag as TagIcon,
    Target,
    Ticket as TicketIcon,
    Handshake as HandshakeIcon,
    Timer,
    Users,
    Vote,
    Workflow as WorkflowIcon,
    Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { PRIVATE_ROUTES } from '@/router/constant/routes';
import { PERMISSION, type Permission } from '@/features/users-roles/constants';

export interface IStaticCommand {
    readonly id: string;
    /** i18n key rendered as t(i18nKey, fallback). */
    readonly i18nKey: string;
    readonly fallback: string;
    readonly icon: LucideIcon;
    readonly url: string;
    readonly keywords: readonly string[];
    readonly kind: 'navigate' | 'create';
    readonly requiredPermission?: Permission;
}

export const STATIC_COMMANDS: readonly IStaticCommand[] = [
    // ---- Create / quick actions (deep links into existing wizards) ----
    {
        id: 'new-campaign',
        i18nKey: 'commandCenter.commands.newCampaign',
        fallback: 'New Campaign',
        icon: Plus,
        url: PRIVATE_ROUTES.CREATE_CAMPAIGN,
        keywords: ['create', 'campaign', 'broadcast', 'send'],
        kind: 'create',
        requiredPermission: PERMISSION.CAMPAIGNS_CREATE,
    },
    {
        id: 'import-contacts',
        i18nKey: 'commandCenter.commands.importContacts',
        fallback: 'Import Contacts',
        icon: Import,
        url: PRIVATE_ROUTES.IMPORT_CONTACTS,
        keywords: ['import', 'csv', 'contacts', 'upload'],
        kind: 'create',
        requiredPermission: PERMISSION.CONTACTS_IMPORT,
    },
    {
        id: 'new-joinlink',
        i18nKey: 'commandCenter.commands.newJoinLink',
        fallback: 'New sign-up link',
        icon: Link2,
        url: PRIVATE_ROUTES.CREATE_JOIN_LINK,
        // 'join link' was the name until this release; it stays searchable.
        keywords: ['create', 'join link', 'join', 'link', 'signup', 'sign-up'],
        kind: 'create',
        requiredPermission: PERMISSION.JOIN_LINKS_CREATE,
    },
    {
        id: 'new-keyword',
        i18nKey: 'commandCenter.commands.newKeyword',
        fallback: 'New text-to-join keyword',
        icon: MessageSquareText,
        url: PRIVATE_ROUTES.CREATE_JOIN_KEYWORD,
        keywords: ['create', 'join keyword', 'keyword', 'text-to-join', 'sms'],
        kind: 'create',
        requiredPermission: PERMISSION.JOIN_KEYWORDS_CREATE,
    },
    {
        id: 'new-form',
        i18nKey: 'commandCenter.commands.newForm',
        fallback: 'New web form',
        icon: FileText,
        url: PRIVATE_ROUTES.FORM_NEW,
        keywords: ['create', 'web form', 'lead form', 'form', 'lead', 'capture'],
        kind: 'create',
        requiredPermission: PERMISSION.LEAD_FORMS_CREATE,
    },
    {
        id: 'new-poll',
        i18nKey: 'commandCenter.commands.newPoll',
        fallback: 'New Poll',
        icon: Vote,
        url: PRIVATE_ROUTES.POLL_CREATE,
        keywords: ['create', 'poll', 'vote', 'survey'],
        kind: 'create',
        requiredPermission: PERMISSION.POLLS_CREATE,
    },
    {
        id: 'new-automation',
        i18nKey: 'commandCenter.commands.newAutomation',
        fallback: 'New Automation',
        icon: WorkflowIcon,
        url: PRIVATE_ROUTES.WORKFLOW_NEW,
        keywords: ['create', 'automation', 'workflow', 'sequence'],
        kind: 'create',
        requiredPermission: PERMISSION.WORKFLOWS_READ,
    },
    {
        // Merging the two lists took Sequences out of the nav; creating one
        // must not have gone with it.
        id: 'new-sequence',
        i18nKey: 'commandCenter.commands.newSequence',
        fallback: 'New Sequence',
        icon: ListOrdered,
        // The template gallery, which leads on to guided setup when wanted.
        url: `${PRIVATE_ROUTES.WORKFLOW_NEW}?type=sequence`,
        keywords: ['create', 'sequence', 'follow-up', 'drip', 'automation'],
        kind: 'create',
        requiredPermission: PERMISSION.SEQUENCES_READ,
    },
    {
        id: 'new-ai-employee',
        i18nKey: 'commandCenter.commands.newAiEmployee',
        fallback: 'New AI Employee',
        icon: Bot,
        url: PRIVATE_ROUTES.AI_EMPLOYEE_NEW,
        keywords: ['create', 'hire', 'ai', 'agent', 'employee'],
        kind: 'create',
        requiredPermission: PERMISSION.AI_AGENTS_READ,
    },
    {
        id: 'new-meeting-type',
        i18nKey: 'commandCenter.commands.newMeetingType',
        fallback: 'New appointment type',
        icon: Calendar,
        url: PRIVATE_ROUTES.MEETING_TYPE_NEW,
        keywords: ['create', 'meeting type', 'meeting', 'booking', 'appointment'],
        kind: 'create',
        requiredPermission: PERMISSION.MEETING_TYPES_CREATE,
    },
    // ---- Navigation ----
    {
        id: 'nav-contacts',
        i18nKey: 'commandCenter.commands.goContacts',
        fallback: 'Contacts',
        icon: Users,
        url: PRIVATE_ROUTES.CONTACTS,
        keywords: ['contacts', 'people', 'customers'],
        kind: 'navigate',
        requiredPermission: PERMISSION.CONTACTS_READ,
    },
    {
        id: 'nav-campaigns',
        i18nKey: 'commandCenter.commands.goCampaigns',
        fallback: 'Campaigns',
        icon: Megaphone,
        url: PRIVATE_ROUTES.CAMPAIGNS,
        keywords: ['campaigns', 'broadcasts', 'marketing'],
        kind: 'navigate',
        requiredPermission: PERMISSION.CAMPAIGNS_READ,
    },
    {
        id: 'nav-inbox',
        i18nKey: 'commandCenter.commands.goInbox',
        fallback: 'Inbox',
        icon: InboxIcon,
        url: PRIVATE_ROUTES.INBOX,
        keywords: ['inbox', 'conversations', 'messages', 'chat'],
        kind: 'navigate',
        requiredPermission: PERMISSION.INBOX_READ,
    },
    {
        id: 'nav-segments',
        i18nKey: 'commandCenter.commands.goSegments',
        fallback: 'Lists',
        icon: ListChecks,
        url: PRIVATE_ROUTES.SEGMENTS,
        keywords: ['segments', 'audiences', 'lists'],
        kind: 'navigate',
        requiredPermission: PERMISSION.SEGMENTS_READ,
    },
    {
        id: 'nav-tags',
        i18nKey: 'commandCenter.commands.goTags',
        fallback: 'Tags',
        icon: TagIcon,
        url: PRIVATE_ROUTES.TAGS,
        keywords: ['tags', 'labels'],
        kind: 'navigate',
        requiredPermission: PERMISSION.TAGS_READ,
    },
    {
        id: 'nav-templates',
        i18nKey: 'commandCenter.commands.goTemplates',
        fallback: 'Message templates',
        icon: FileText,
        url: PRIVATE_ROUTES.TEMPLATES,
        keywords: ['templates', 'sms', 'email'],
        kind: 'navigate',
        requiredPermission: PERMISSION.SMS_TEMPLATES_READ,
    },
    {
        id: 'nav-outcomes',
        i18nKey: 'commandCenter.commands.goOutcomes',
        fallback: 'Outcomes',
        icon: Target,
        url: PRIVATE_ROUTES.OUTCOMES,
        keywords: ['outcomes', 'leads', 'funnel', 'booked', 'won', 'report'],
        kind: 'navigate',
        requiredPermission: PERMISSION.ANALYTICS_READ,
    },
    {
        id: 'nav-analytics',
        i18nKey: 'commandCenter.commands.goAnalytics',
        // The menu calls this section "Reports"; the palette said "Analytics", so
        // the one place somebody searches for a page disagreed with the one place
        // they read its name. The old English word stays a keyword.
        fallback: 'Reports',
        icon: BarChart3,
        url: PRIVATE_ROUTES.ANALYTICS_OVERVIEW,
        keywords: ['analytics', 'reports', 'stats', 'performance'],
        kind: 'navigate',
        requiredPermission: PERMISSION.ANALYTICS_READ,
    },
    {
        id: 'nav-automations',
        i18nKey: 'commandCenter.commands.goAutomations',
        fallback: 'Automations',
        icon: WorkflowIcon,
        url: PRIVATE_ROUTES.AUTOMATIONS,
        // The old names stay searchable: they were the nav labels until this
        // release and are what half the team will still type.
        keywords: ['automations', 'workflows', 'sequences', 'playbooks'],
        kind: 'navigate',
        requiredPermission: PERMISSION.WORKFLOWS_READ,
    },
    {
        id: 'nav-ai-employees',
        i18nKey: 'commandCenter.commands.goAiEmployees',
        fallback: 'AI Employees',
        icon: Bot,
        url: PRIVATE_ROUTES.AI_EMPLOYEES,
        // Phase 10: the phone is an employee's channel, so somebody looking
        // for "the phone" — what it does, not a product name — lands here.
        keywords: [
            'ai',
            'employees',
            'agents',
            'bots',
            'voice',
            'phone',
            'calls',
            'receptionist',
            'answering',
            'ai phone',
        ],
        kind: 'navigate',
        requiredPermission: PERMISSION.AI_AGENTS_READ,
    },
    {
        // Neither of the next two was in the palette before. Both were renamed
        // in this release, so the old name needs somewhere to land.
        id: 'nav-knowledge-bases',
        i18nKey: 'commandCenter.commands.goKnowledgeBases',
        fallback: 'What the AI knows',
        icon: BookOpen,
        url: PRIVATE_ROUTES.KNOWLEDGE_BASES,
        keywords: ['knowledge base', 'knowledge', 'answers', 'ai', 'faq', 'documents'],
        kind: 'navigate',
        requiredPermission: PERMISSION.KNOWLEDGE_BASES_READ,
    },
    {
        id: 'nav-availability',
        i18nKey: 'commandCenter.commands.goAvailability',
        fallback: 'Working hours',
        icon: Clock,
        url: PRIVATE_ROUTES.AVAILABILITY,
        keywords: ['availability', 'working hours', 'hours', 'schedule', 'when'],
        kind: 'navigate',
        requiredPermission: PERMISSION.AVAILABILITY_MANAGE,
    },
    {
        id: 'nav-calendar',
        i18nKey: 'commandCenter.commands.goCalendar',
        fallback: 'Calendar',
        icon: Calendar,
        url: PRIVATE_ROUTES.CALENDAR,
        keywords: ['calendar', 'bookings', 'appointments', 'schedule'],
        kind: 'navigate',
        requiredPermission: PERMISSION.CALENDAR_READ,
    },
    {
        id: 'nav-tickets',
        i18nKey: 'commandCenter.commands.goTickets',
        fallback: 'Support tickets',
        icon: TicketIcon,
        url: PRIVATE_ROUTES.TICKETS,
        keywords: ['tickets', 'support', 'issues'],
        kind: 'navigate',
        requiredPermission: PERMISSION.TICKETS_READ,
    },
    {
        id: 'nav-opportunities',
        i18nKey: 'commandCenter.commands.goOpportunities',
        fallback: 'All deals',
        icon: HandshakeIcon,
        url: PRIVATE_ROUTES.OPPORTUNITIES,
        keywords: [
            'opportunities',
            'deals',
            'pipeline',
            'sales',
            'crm',
            // The Sales stages page (/pipelines) folded into the board's
            // Customize pipeline sheet; its old words land here.
            'pipelines',
            'sales stages',
            'customize pipeline',
        ],
        kind: 'navigate',
        requiredPermission: PERMISSION.OPPORTUNITIES_READ,
    },
    {
        id: 'nav-today',
        i18nKey: 'commandCenter.commands.goToday',
        fallback: 'Daily Updates',
        icon: CalendarDays,
        url: PRIVATE_ROUTES.TODAY,
        // 'today' is the retired name and stays searchable forever
        // (static-commands-old-names.test.ts).
        keywords: ['today', 'daily', 'updates', 'home', 'leads', 'queue'],
        kind: 'navigate',
    },
    // WhatsApp lives in three places (the Channels hub, its own settings page, its
    // templates), none named after it in the menu — so "WhatsApp" found nothing here.
    {
        id: 'nav-channels',
        i18nKey: 'commandCenter.commands.goChannels',
        fallback: 'Channels',
        icon: Radio,
        url: PRIVATE_ROUTES.CHANNELS,
        keywords: ['channels', 'whatsapp', 'sms', 'text', 'email', 'web chat', 'phone', 'connect'],
        kind: 'navigate',
        requiredPermission: PERMISSION.CHANNELS_READ,
    },
    {
        id: 'nav-settings-whatsapp',
        i18nKey: 'commandCenter.commands.goWhatsApp',
        fallback: 'WhatsApp settings',
        icon: MessageCircle,
        url: PRIVATE_ROUTES.SETTINGS_WHATSAPP,
        keywords: ['whatsapp', 'meta', 'business account', 'connect whatsapp'],
        kind: 'navigate',
        requiredPermission: PERMISSION.CHANNELS_READ,
    },
    {
        id: 'nav-whatsapp-templates',
        i18nKey: 'commandCenter.commands.goWhatsAppTemplates',
        fallback: 'WhatsApp templates',
        icon: MessageCircle,
        url: PRIVATE_ROUTES.TEMPLATES_WHATSAPP,
        keywords: ['whatsapp', 'template', 'templates', 'meta approval'],
        kind: 'navigate',
        requiredPermission: PERMISSION.WHATSAPP_TEMPLATES_READ,
    },
    {
        id: 'nav-settings-channels',
        i18nKey: 'commandCenter.commands.goMessagingChannels',
        fallback: 'Messaging channels',
        icon: Radio,
        url: PRIVATE_ROUTES.SETTINGS_CHANNELS,
        keywords: ['twilio', 'telnyx', 'numbers', 'phone numbers', 'sms', '10dlc', 'messaging'],
        kind: 'navigate',
        requiredPermission: PERMISSION.CHANNELS_READ,
    },
    {
        // `HOME` now redirects to Today, so this entry names the route it
        // actually opens rather than following "home" somewhere else.
        id: 'nav-dashboard',
        i18nKey: 'commandCenter.commands.goDashboard',
        fallback: 'My dashboards',
        icon: LayoutDashboard,
        url: PRIVATE_ROUTES.DASHBOARD,
        keywords: ['dashboard', 'dashboards', 'overview', 'widgets'],
        kind: 'navigate',
    },
    {
        id: 'nav-settings',
        i18nKey: 'commandCenter.commands.goSettings',
        fallback: 'Settings',
        icon: Settings,
        url: PRIVATE_ROUTES.SETTINGS,
        keywords: ['settings', 'preferences', 'configuration'],
        kind: 'navigate',
    },
    {
        id: 'nav-settings-org',
        i18nKey: 'commandCenter.commands.goOrgSettings',
        fallback: 'Organization Settings',
        icon: Settings,
        url: PRIVATE_ROUTES.SETTINGS_ORGANIZATION,
        keywords: ['organization', 'workspace', 'company'],
        kind: 'navigate',
        requiredPermission: PERMISSION.ORGANIZATION_READ,
    },
    {
        id: 'nav-settings-users',
        i18nKey: 'commandCenter.commands.goTeam',
        fallback: 'Team Members',
        icon: Users,
        url: PRIVATE_ROUTES.SETTINGS_USERS_MEMBERS,
        keywords: ['team', 'users', 'members', 'invite'],
        kind: 'navigate',
        requiredPermission: PERMISSION.USERS_READ,
    },
    {
        id: 'nav-settings-api-keys',
        i18nKey: 'commandCenter.commands.goApiKeys',
        fallback: 'API Keys',
        icon: KeyRound,
        url: PRIVATE_ROUTES.SETTINGS_API_KEYS,
        keywords: ['api', 'keys', 'developer', 'integration'],
        kind: 'navigate',
        requiredPermission: PERMISSION.API_KEYS_READ,
    },
    {
        id: 'nav-settings-field-service',
        i18nKey: 'commandCenter.commands.goFieldService',
        fallback: 'Connected field service',
        icon: Wrench,
        url: PRIVATE_ROUTES.SETTINGS_FIELD_SERVICE,
        // The two systems by name: somebody looking for this types "jobber",
        // not "field service".
        keywords: ['field', 'service', 'jobber', 'jobnimbus', 'jobs', 'estimates'],
        kind: 'navigate',
        requiredPermission: PERMISSION.FIELD_SERVICE_READ,
    },
    {
        id: 'nav-settings-voice-minutes',
        i18nKey: 'commandCenter.commands.goVoiceMinutes',
        fallback: 'Voice minutes',
        icon: Timer,
        url: PRIVATE_ROUTES.SETTINGS_VOICE_MINUTES,
        // The workspace's answered minutes and the monthly warning (D13).
        keywords: ['minutes', 'voice minutes', 'alert', 'usage', 'ai phone'],
        kind: 'navigate',
        requiredPermission: PERMISSION.VOICE_CONFIG_READ,
    },
    {
        id: 'nav-billing',
        i18nKey: 'commandCenter.commands.goBilling',
        fallback: 'Billing',
        icon: CreditCard,
        url: PRIVATE_ROUTES.BILLING,
        keywords: ['billing', 'subscription', 'credits', 'wallet', 'payment'],
        kind: 'navigate',
        requiredPermission: PERMISSION.BILLING_READ,
    },
];

/**
 * Filter commands by permission then by query (title + keywords substring,
 * case-insensitive). Empty query returns all permitted commands — used for
 * the Suggested empty state.
 */
export function matchStaticCommands(
    commands: readonly IStaticCommand[],
    query: string,
    hasPermission: (p: Permission) => boolean,
): IStaticCommand[] {
    const q = query.trim().toLowerCase();
    return commands.filter((cmd) => {
        if (cmd.requiredPermission && !hasPermission(cmd.requiredPermission)) return false;
        if (q.length === 0) return true;
        if (cmd.fallback.toLowerCase().includes(q)) return true;
        return cmd.keywords.some((k) => k.includes(q) || q.includes(k));
    });
}
